import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import Header from '../../../shared/Header';
import { Stack } from '../../../shared/layouts';
import Placeholder from '../../../shared/Placeholder';
import Loader from '../../../shared/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../../shared/BackButton';
import Button from '../../../shared/Button';
import MessagebarContext from '../../../../context/MessagebarContext';
import { useTranslation } from 'react-i18next';
import DescriptionMessage from '../../../shared/DescriptionMessage';
import OcktoLogo from 'imagesUwKluis/svg/ockto.svg';
import VeiligOphalenLogo from 'imagesUwKluis/svg/vo_logo.svg';
import QrScanningSection from './QrScanningSection';
import ResultSection from './ResultSection';
import RetrievingDataSection from './RetrievingDataSection';
import UnavailableDataSourcesModal from './UnavailableDataSourcesModal';
import DownloadAppModal from '../../../shared/DownloadAppModal';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@dnhb/node_package';
import useIconLibrary from '../../../../hooks/useIconLibrary';
import IntroSection from './IntroSection';

const STATUS_CHECK_INTERVAL = 2; //seconds

const getSourceTypeDetails = (iconLibrary, type) => {
  const { VeiligOphalenIcon, OcktoIcon } = iconLibrary;

  const sourceTypes = {
    veiligophalen: {
      app_name: 'Veilig Ophalen',
      icon: <VeiligOphalenIcon height="2rem" width="2rem" />,
      logo: <VeiligOphalenLogo height="32" />,
      play_store_link:
        'play.google.com/store/apps/details?id=nl.veiligophalen&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1',
      app_store_link:
        'apps.apple.com/us/app/veiligophalen-nl/id1618776371?itsct=apps_box_badge&amp;itscg=30200',
      url: 'veiligophalen.nl'
    },
    ockto: {
      app_name: 'Ockto',
      icon: <OcktoIcon height="2rem" width="2rem" />,
      logo: <OcktoLogo height="32" />,
      play_store_link: 'android.ockto.nl',
      app_store_link: 'ios.ockto.nl',
      url: 'ockto.eu'
    }
  };

  return sourceTypes[type] || null;
};

const SourceDataDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestId } = useParams();
  const { setCustomMessage } = useContext(MessagebarContext);
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const iconLibrary = useIconLibrary();
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [isPrepared, setIsPrepared] = useState(false);
  const [isScanned, setIsScanned] = useState(false);
  const [showDownloadAppModal, setShowDownloadAppModal] = useState(false);
  const [
    showUnavailableDataSourcesModal,
    setShowUnavailableDataSourcesModal
  ] = useState(false);
  const [unavailableDataSources, setUnavailableDataSources] = useState([]);
  const [sourceType, setSourceType] = useState({
    title: t('Gegevens ophalen'),
    app_name: '',
    play_store_link: null,
    app_store_link: null,
    icon: null,
    logo: null,
    url: ''
  });

  const {
    isError,
    refetch,
    isLoading,
    isFetching,
    data: {
      title,
      description,
      url,
      qr,
      created_at,
      completed_at,
      available_data_sources,
      assigned_person_name,
      type
    } = {}
  } = useQuery(
    ['SourceDataRequestItem', requestId],
    async () => {
      const { data } = await axios.get(`/source-data/${requestId}`);
      if (!initialLoaded && data.unavailable_data_sources.length > 0) {
        setUnavailableDataSources(data.unavailable_data_sources);
        setShowUnavailableDataSourcesModal(true);
      }
      setSourceType(getSourceTypeDetails(iconLibrary, data.type));
      setInitialLoaded(true);
      return data;
    },
    {
      refetchOnWindowFocus: false
    }
  );

  const { data: status } = useQuery(
    ['SourceDataRequestCompleted', requestId],
    async () => {
      const { data } = await axios.get(`/source-data/${requestId}/status`);
      return data;
    },
    {
      enabled: !!requestId && !completed_at,
      refetchOnWindowFocus: false,
      refetchInterval: STATUS_CHECK_INTERVAL * 1000
    }
  );

  useEffect(() => {
    if (status?.completed) {
      refetch();
    } else if (type === 'ockto') {
      setIsScanned(isMinDesktop ? status?.scanned : true);
    }
  }, [status, refetch]);

  useEffect(() => {
    setIsScanned(false);
    if (!isMinDesktop && type === 'ockto') setIsScanned(true);
  }, [initialLoaded, type]);

  return (
    <>
      {(!initialLoaded || isLoading) && <Loader />}
      <DownloadAppModal
        showDownloadAppModal={showDownloadAppModal}
        setShowDownloadAppModal={setShowDownloadAppModal}
        appInfo={sourceType}
      />
      <UnavailableDataSourcesModal
        showUnvailableDataSourcesModal={showUnavailableDataSourcesModal}
        setShowUnavailableDataSourcesModal={setShowUnavailableDataSourcesModal}
        unavailableDataSources={unavailableDataSources}
      />
      <Stack space="xl" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        {requestId !== null && (
          <BackButton onClick={() => navigate('/requests')}>
            {t('Terug')}
          </BackButton>
        )}
        <Header
          title={
            (title || '') +
            (assigned_person_name ? ` (${assigned_person_name})` : '')
          }
          iconColor={null}
          icon={sourceType.icon}
          variant="page"
          button={
            completed_at && isMinDesktop ? (
              <Button
                variant={'primary-outline'}
                onClick={() => {
                  window.location.href = `/source-data/${requestId}/download`;
                  setCustomMessage(
                    t('De download is gestart. Een moment geduld.')
                  );
                }}
              >
                {t('Download brondata')}
              </Button>
            ) : null
          }
        />
        {description && <DescriptionMessage>{description}</DescriptionMessage>}
        {isError ? (
          <Placeholder>
            {t('Er is iets mis gegaan met het ophalen van deze brondata.')}
          </Placeholder>
        ) : (
          <Stack space="m">
            {!completed_at && !isPrepared && (
              <IntroSection {...{ sourceType, setIsPrepared }} />
            )}
            {!completed_at && isPrepared && !isScanned && (
              <QrScanningSection
                {...{
                  isFetching,
                  refetch,
                  url,
                  qr,
                  setShowDownloadAppModal,
                  sourceType,
                  type
                }}
              />
            )}
            {!completed_at && isPrepared && isScanned && (
              <RetrievingDataSection
                {...{
                  sourceType,
                  isFetching,
                  isLoading,
                  refetch,
                  url,
                  qr,
                  available_data_sources
                }}
              />
            )}
            {completed_at && (
              <ResultSection
                {...{
                  description,
                  created_at,
                  completed_at,
                  requestId,
                  sourceType
                }}
              />
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default SourceDataDetailPage;
